import React, {useState} from 'react';
import MediaLibraryManager from '../../Media/MediaLibraryManager';

export default function ContentGallery({items = [], onAdd, onUpdate, onRemove, onOrder}) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [caption, setCaption] = useState('');
    const [altText, setAltText] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const run = async action => {
        setBusy(true);
        setError('');
        try {
            await action();
        } catch (nextError) {
            setError(nextError.message);
        } finally {
            setBusy(false);
        }
    };

    const move = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= items.length) return;
        const next = [...items];
        [next[index], next[target]] = [next[target], next[index]];
        run(() => onOrder(next.map(item => item.uuid)));
    };

    return <section className="content-editor-panel" aria-labelledby="content-gallery-title">
        <header className="content-editor-panel-header">
            <div>
                <h2 id="content-gallery-title">Gallery</h2>
                <p>Add images or videos below the page content.</p>
            </div>
            <button type="button" className="button" onClick={() => setPickerOpen(true)}>
                Add media
            </button>
        </header>
        {error && <p className="flash error" role="alert">{error}</p>}
        {items.length === 0 && <p className="help">No gallery items yet.</p>}
        <ol className="content-media-list">
            {items.map((item, index) => <li key={item.uuid}>
                {item.file?.mimetype?.startsWith('image/')
                    ? <img src={item.file.cdnUrl} alt={item.altText || item.description || ''} />
                    : <span className="content-media-file">{item.file?.filename || 'Video file'}</span>}
                <div>
                    <strong>{item.description || item.file?.filename || 'Untitled media'}</strong>
                    {item.altText && <small>Alt: {item.altText}</small>}
                </div>
                <div className="content-media-actions">
                    <button type="button" className="button small" disabled={busy}
                        onClick={() => {
                            setEditingItem(item);
                            setSelected(item.file);
                            setCaption(item.description || '');
                            setAltText(item.altText || '');
                            setPickerOpen(true);
                        }}>Edit</button>
                    <button type="button" className="button small" disabled={busy || index === 0}
                        aria-label={`Move ${item.description || 'media'} up`}
                        onClick={() => move(index, -1)}>Up</button>
                    <button type="button" className="button small"
                        disabled={busy || index === items.length - 1}
                        aria-label={`Move ${item.description || 'media'} down`}
                        onClick={() => move(index, 1)}>Down</button>
                    <button type="button" className="button small danger" disabled={busy}
                        onClick={() => run(() => onRemove(item.uuid))}>Remove</button>
                </div>
            </li>)}
        </ol>
        {pickerOpen && !selected && <MediaLibraryManager
            variant="modal"
            isOpen
            selectable
            accept="image/*,video/*"
            title="Choose gallery media"
            onSelect={file => {
                setEditingItem(null);
                setSelected(file);
                setAltText(file.metadata?.altText || file.description || '');
            }}
            onCancel={() => setPickerOpen(false)}
        />}
        {pickerOpen && selected && <div className="media-system-modal-backdrop" role="presentation">
            <div className="content-inline-alt-dialog" role="dialog" aria-modal="true"
                aria-labelledby="gallery-details-title">
                <h2 id="gallery-details-title">Gallery details</h2>
                <label htmlFor="gallery-caption">Caption</label>
                <input id="gallery-caption" value={caption}
                    onChange={event => setCaption(event.target.value)} />
                {selected.mimetype?.startsWith('image/') && <>
                    <label htmlFor="gallery-alt">Alt text</label>
                    <input id="gallery-alt" value={altText}
                        onChange={event => setAltText(event.target.value)} />
                </>}
                <div className="content-dialog-actions">
                    <button type="button" className="button secondary" onClick={() => {
                        setSelected(null);
                        setEditingItem(null);
                    }}>
                        Back
                    </button>
                    <button type="button" className="button" disabled={busy}
                        onClick={() => run(async () => {
                            if (editingItem) {
                                await onUpdate(editingItem.uuid, {
                                    fileId: selected.id, description: caption, altText,
                                });
                            } else {
                                await onAdd({fileId: selected.id, description: caption, altText});
                            }
                            setPickerOpen(false);
                            setSelected(null);
                            setEditingItem(null);
                            setCaption('');
                            setAltText('');
                        })}>{editingItem ? 'Save gallery item' : 'Add to gallery'}</button>
                </div>
            </div>
        </div>}
    </section>;
}
